import React, { useState, useEffect } from "react";
import {
  TicketStatus,
  TicketFilters,
  TICKET_STATUSES,
  TICKET_TYPES,
  TICKET_PRIORITIES,
  NO_LABEL,
} from "./types";
import { Author } from "api/author";
import { LabelCondition } from "api/tickets";
import { TicketSearchParams } from "./hooks/useTicketQueries";

// Assignee and creator fields hold either a user id or a user name, so a
// server-side match has to look for every alias of the selected users.
export function collectUserAliases(
  userIds: string[],
  users: Author[],
): string[] {
  const aliases = new Set<string>();
  for (const userId of userIds) {
    aliases.add(userId);
    const name = users.find((user) => user.id === userId)?.name?.trim();
    if (name) aliases.add(name);
  }
  return Array.from(aliases);
}

// The free-text search also matches a ticket by its reporter's name, which only
// the client can resolve — the server gets the matching creator values instead.
function collectCreatorsMatchingQuery(
  searchQuery: string,
  users: Author[],
): string[] {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (!normalizedQuery) return [];
  const matchedIds = users
    .filter((user) => user.name?.toLowerCase().includes(normalizedQuery))
    .map((user) => user.id);
  return collectUserAliases(matchedIds, users);
}

function toDayStart(dateString: string): string | undefined {
  if (!dateString) return undefined;
  return new Date(`${dateString}T00:00:00`).toISOString();
}

function toDayEnd(dateString: string): string | undefined {
  if (!dateString) return undefined;
  return new Date(`${dateString}T23:59:59.999`).toISOString();
}

function emptyToUndefined<T>(values: T[]): T[] | undefined {
  return values.length > 0 ? values : undefined;
}

export type TicketSearchScope = {
  filters: TicketFilters;
  searchQuery: string;
  selectedAssignees: string[];
  selectedPanelLabels: string[];
  users: Author[];
  isArchived: boolean;
  onlyActive?: boolean;
  onlyUnassigned?: boolean;
  excludeIdeas?: boolean;
};

/**
 * Translates the toolbar, filter panel and label panel into one server-side
 * query. Nothing is filtered in the browser, so a view never depends on how
 * many pages happen to be loaded.
 */
export function buildTicketSearchParams({
  filters,
  searchQuery,
  selectedAssignees,
  selectedPanelLabels,
  users,
  isArchived,
  onlyActive = false,
  onlyUnassigned = false,
  excludeIdeas = false,
}: TicketSearchScope): TicketSearchParams {
  const labelConditions: LabelCondition[] = [];
  if (filters.labels.length > 0) {
    labelConditions.push({ anyOf: filters.labels });
  }
  if (selectedPanelLabels.length > 0) {
    labelConditions.push({
      anyOf: selectedPanelLabels.filter((label) => label !== NO_LABEL),
      includeUnlabeled: selectedPanelLabels.includes(NO_LABEL),
    });
  }

  const excludeStatuses: TicketStatus[] = [];
  if (excludeIdeas) excludeStatuses.push("idea");
  if (onlyActive) excludeStatuses.push("done", "hold");

  return {
    query: searchQuery.trim(),
    filters: {
      isArchived,
      statuses: emptyToUndefined(filters.statuses),
      excludeStatuses: emptyToUndefined(excludeStatuses),
      types: emptyToUndefined(filters.types),
      priorities: emptyToUndefined(filters.priorities),
      labelConditions: emptyToUndefined(labelConditions),
      assignees: emptyToUndefined(collectUserAliases(selectedAssignees, users)),
      includeUnassigned: onlyUnassigned || undefined,
      assigneeSearch: filters.assignee.trim() || undefined,
      reporters: emptyToUndefined(collectUserAliases(filters.reporters, users)),
      targetVersion: filters.targetVersion.trim() || undefined,
      updatedFrom: toDayStart(filters.updatedFrom),
      updatedTo: toDayEnd(filters.updatedTo),
      searchCreators: emptyToUndefined(
        collectCreatorsMatchingQuery(searchQuery, users),
      ),
    },
  };
}

let counter = 0;

export function generateId(): string {
  counter += 1;
  return `${Date.now()}-${counter}`;
}

export function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const differenceMilliseconds = now.getTime() - date.getTime();
  const differenceMinutes = Math.floor(differenceMilliseconds / 60000);
  const differenceHours = Math.floor(differenceMinutes / 60);
  const differenceDays = Math.floor(differenceHours / 24);

  if (differenceMinutes < 1) return "just now";
  if (differenceMinutes < 60)
    return `${differenceMinutes} minute${differenceMinutes === 1 ? "" : "s"} ago`;
  if (differenceHours < 24)
    return `${differenceHours} hour${differenceHours === 1 ? "" : "s"} ago`;
  if (differenceDays < 30)
    return `${differenceDays} day${differenceDays === 1 ? "" : "s"} ago`;
  return formatDate(isoString);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function calculateInsertionOrder(
  prev: { order?: number } | null,
  next: { order?: number } | null,
): number {
  if (!prev && !next) return 0;
  if (!prev) return (next?.order ?? 0) - 1;
  if (!next) return (prev?.order ?? 0) + 1;
  return ((prev?.order ?? 0) + (next?.order ?? 0)) / 2;
}

export function getStatusLabel(status: string): string {
  return (
    TICKET_STATUSES.find((statusDef) => statusDef.value === status)?.label ??
    status
  );
}

export function getTypeLabel(type: string): string {
  return TICKET_TYPES.find((typeDef) => typeDef.value === type)?.label ?? type;
}

export function getPriorityLabel(priority: string): string {
  return (
    TICKET_PRIORITIES.find((priorityDef) => priorityDef.value === priority)
      ?.label ?? priority
  );
}

const STATUS_ORDER: TicketStatus[] = TICKET_STATUSES.map(
  (statusDef) => statusDef.value,
);

export function getNextStatus(current: TicketStatus): TicketStatus | null {
  const index = STATUS_ORDER.indexOf(current);
  if (index < 0 || index >= STATUS_ORDER.length - 1) return null;
  return STATUS_ORDER[index + 1];
}

export function sanitizeVersion(value: string): string {
  return value.replace(/[^0-9.]/g, "");
}

export function isClockTicketMissingVersion(ticket: {
  labels: string[];
  targetVersion: string;
}): boolean {
  return ticket.labels.includes("clock") && !ticket.targetVersion.trim();
}

export function canChangeStatus(
  ticket: { labels: string[]; targetVersion: string },
  newStatus: TicketStatus,
): boolean {
  if (newStatus === "done" && isClockTicketMissingVersion(ticket)) {
    alert("Clock tickets require a target version before moving to Done.");
    return false;
  }
  return true;
}

export function getNextStatusLabel(current: TicketStatus): string | null {
  const next = getNextStatus(current);
  if (!next) return null;
  return (
    TICKET_STATUSES.find((statusDef) => statusDef.value === next)?.label ?? null
  );
}

export function isImage(mimeType?: string): boolean {
  return !!mimeType?.startsWith("image/");
}

export function isVideo(mimeType?: string): boolean {
  return !!mimeType?.startsWith("video/");
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// All entries must read well behind white avatar initials. Avoid the neutral
// `secondary.*` tokens — they're near-white in light mode and make the text
// disappear.
export const AVATAR_COLORS = [
  "info.dark",
  "success.dark",
  "error.dark",
  "#7E57C2",
  "warning.dark",
  "info.main",
  "grey.700",
  "grey.600",
];

export function getInitials(name: string): string {
  if (!name.trim()) return "?";
  return name
    .trim()
    .split(" ")
    .filter((part) => part.length > 0)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getAssignableUsers(users: Author[]): Author[] {
  return users.filter(
    (user) => user.role === "admin" || user.role === "viewer",
  );
}

export function findUserByAssignee(
  assignee: string,
  users: Author[],
): Author | null {
  if (!assignee) return null;
  return (
    users.find(
      (user) => user.id === assignee || user.name?.trim() === assignee.trim(),
    ) ?? null
  );
}

export function buildUserLookup(users: Author[]): Map<string, Author> {
  const lookup = new Map<string, Author>();
  for (const user of users) {
    lookup.set(user.id, user);
    const trimmedName = user.name?.trim();
    if (trimmedName) lookup.set(trimmedName, user);
  }
  return lookup;
}

export function resolveUserName(assignee: string, users: Author[]): string {
  if (!assignee) return "";
  return findUserByAssignee(assignee, users)?.name.trim() ?? assignee;
}

export function resolveUserId(assignee: string, users: Author[]): string {
  if (!assignee) return "";
  return findUserByAssignee(assignee, users)?.id ?? assignee;
}

export function useOptimisticState<T>(
  computedValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState(computedValue);
  useEffect(() => {
    setState(computedValue);
  }, [computedValue]);
  return [state, setState];
}
